import ExampleList from './components/ExampleList';
import FilePreview from './components/FilePreview';
import FileUpload from './components/FileUpload';
import DlpToggle from './components/DlpToggle';
import SendButton from './components/SendButton';
import ToggleInputFeaturesButton from './components/ToggleInputFeaturesButton';
import MessageInput from './components/MessageInput';
import useChatInput, { MAX_MESSAGE_LENGTH } from '../../../Hooks/useChatInput';
import { Chat } from '../../../Types';
import { Button } from '../../Utility/Button';
import DlpAcitve from '../../Utility/Icons/DlpActive';
import DlpInactive from '../../Utility/Icons/DlpInactive';
import { DLPState } from '../../../Types/Enums';
import { useState } from 'react';
import RecordButton from './components/RecordButton';
import MobileRecordButton from './components/MobileRecordButton';
import useModal from '../../Modal/useModal';

interface ChatProps {
    chat?: Chat;
    giveExamples?: boolean;
    initMessage?: string;
}

export default function ChatInput({ chat = {} as Chat, giveExamples = false, initMessage }: ChatProps) {
    const {
        ChatInputActions,
        isPreviewPrompt,
        tmpImageUrls,
        tmpFileNames,
        files,
        fileHoverIndex,
        setFileHoverIndex,
        handleFileDelete,
        newMessage,
        numOfLines,
        showIcons,
        ToggleInputFeaturesRef,
        showExamples,
        selectedPrompt,
        i18n,
        textInputRef,
        height,
        isSending,
        isStopable,
        isDlpChecking,
        showPiiText,
        setShowPiiText,
        setShouldSendAfterDlpChange,
        fullDlpBlocked,
        sendMessage,
        handleTextareaClick,
        fileTypes,
        setNewMessage,
    } = useChatInput({ chat, initMessage });

    const isOverCharacterLimit = newMessage.length > MAX_MESSAGE_LENGTH;
    const characterLimitTooltip = isOverCharacterLimit
        ? i18n.translate({
              id: 'messageLengthLimit.tooltip',
              sourceName: 'Chat',
              params: { current: newMessage.length, max: MAX_MESSAGE_LENGTH },
          })
        : '';

    const [isRecording, setIsRecording] = useState(false);
    const [audioVolume, setAudioVolume] = useState(0);
    const modal = useModal();

    return (
        <>
            <FilePreview
                files={files}
                fileHoverIndex={fileHoverIndex}
                setFileHoverIndex={setFileHoverIndex}
                handleFileDelete={handleFileDelete}
            />
            <div
                className={`joyride-step-chat-input ${
                    showPiiText ? '' : 'flex'
                } relative justify-between items-end sm:items-center
                        sm:gap-2.5 gap-2 sm:mb-0  sm:py-0 w-full mx-auto sm:bg-chatInputHome-background py-2 sm:bg-transaprent ${
                            showPiiText ? 'px-3 md:px-0' : isRecording ? 'px-4 md:px-0' : 'pr-0'
                        } sm:border-none border-t border-layout-borderColor
                        ${numOfLines > 2 ? 'sm:rounded-[1rem]' : 'sm:rounded-[2rem]'}
                        ${isPreviewPrompt ? 'sm:bg-input-backgroundColor' : 'sm:bg-chatInputHome-background'}
                    `}
            >
                {!isRecording && (
                    <div className="flex sm:hidden w-fit transition-all duration-[300ms]">
                        <div
                            style={{
                                width: ChatInputActions.calculateWidth(),
                            }}
                            className={`transition-all duration-[300ms] pl-1 overflow-x-hidden ${
                                showPiiText ? 'hidden' : ''
                            }`}
                        >
                            <div ref={ToggleInputFeaturesRef} className="flex w-fit gap-0 h-[60px]">
                                <ToggleInputFeaturesButton
                                    messageLength={newMessage.length}
                                    showIcons={showIcons}
                                    handlePlusClick={ChatInputActions.handlePlusClick}
                                    handleCrossClick={ChatInputActions.handleCrossClick}
                                />
                                {selectedPrompt?.chatUploadEnabled && Object.keys(fileTypes).length > 0 && (
                                    <FileUpload
                                        handleFileChange={ChatInputActions.handleFileChange}
                                        fileTypes={fileTypes}
                                    />
                                )}
                                {selectedPrompt!.combinedDlp !== DLPState.dlp_off && (
                                    <DlpToggle
                                        dlpActive={true}
                                        handleDlpActiveCheckbox={ChatInputActions.handleDlpChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <MessageInput
                    handleInputChange={ChatInputActions.handleInputChange}
                    handleKeyDown={ChatInputActions.handleKeyDown}
                    handlePaste={ChatInputActions.handlePaste}
                    newMessage={newMessage}
                    numOfLines={numOfLines}
                    i18n={i18n}
                    textInputRef={textInputRef}
                    isPreviewPrompt={isPreviewPrompt}
                    showPiiText={showPiiText}
                    onTextareaClick={handleTextareaClick}
                    isRecording={isRecording}
                    volume={audioVolume}
                    isSending={isSending}
                    onStartRecording={() => {
                        if ((window as any).__mobileRecordingStart) {
                            (window as any).__mobileRecordingStart();
                        }
                    }}
                />

                {!showPiiText ? (
                    <>
                        <div
                            style={{ height: height }}
                            className={`hidden sm:flex flex-row items-end gap-0 my-auto pb-0`}
                        >
                            {!isRecording && (
                                <div className="flex justify-center items-center">
                                    {selectedPrompt?.chatUploadEnabled && Object.keys(fileTypes).length > 0 && (
                                        <div className="joyride-step-chat-fileUpload">
                                            <FileUpload
                                                handleFileChange={ChatInputActions.handleFileChange}
                                                fileTypes={fileTypes}
                                            />
                                        </div>
                                    )}
                                    {selectedPrompt!.combinedDlp !== DLPState.dlp_off && (
                                        <DlpToggle
                                            dlpActive={true}
                                            handleDlpActiveCheckbox={ChatInputActions.handleDlpChange}
                                        />
                                    )}
                                </div>
                            )}

                            <RecordButton
                                onRecordingChange={setIsRecording}
                                onVolumeChange={setAudioVolume}
                                setNewMessage={setNewMessage}
                                newMessage={newMessage}
                                i18n={i18n}
                            />

                            {!isRecording && (
                                <SendButton
                                    numOfLines={numOfLines}
                                    isSending={isSending}
                                    isStoppable={isStopable}
                                    isDlpChecking={isDlpChecking}
                                    stopStream={ChatInputActions.stopStream}
                                    sendMessageHandler={ChatInputActions.handleDlpSendMessage}
                                    buttonStyle={true}
                                    isOverCharacterLimit={isOverCharacterLimit}
                                    characterLimitTooltip={characterLimitTooltip}
                                />
                            )}
                        </div>

                        {/* Mobile Send Button - nur wenn nicht aufgenommen wird */}
                        {!isRecording && (
                            <SendButton
                                numOfLines={numOfLines}
                                isSending={isSending}
                                isStoppable={isStopable}
                                isDlpChecking={isDlpChecking}
                                stopStream={ChatInputActions.stopStream}
                                sendMessageHandler={ChatInputActions.handleDlpSendMessage}
                                buttonStyle={false}
                                visibilityClass={`block sm:hidden`}
                                isOverCharacterLimit={isOverCharacterLimit}
                                characterLimitTooltip={characterLimitTooltip}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <div className="pb-2 md:pb-4 md:px-6">
                            <hr className="bg-gray-600 border-gray-600 mt-0 mb-4" />
                            <p>{i18n.translate({ id: 'dlp.warning.text', sourceName: 'Chat' })}</p>
                            <div className="md:flex md:gap-4 pt-2 md:pt-4">
                                {!fullDlpBlocked && (
                                    <Button
                                        onClick={() => {
                                            setShowPiiText(false);
                                            sendMessage();
                                        }}
                                        className="mb-4 md:mb-0 mr-4 md:mr-0 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover"
                                        variant="secondary"
                                    >
                                        <div className="flex items-center gap-2">
                                            <DlpAcitve size={20} />
                                            <span>
                                                {i18n.translate({
                                                    id: 'dlp.button.send.anonymized',
                                                    sourceName: 'Chat',
                                                })}
                                            </span>
                                        </div>
                                    </Button>
                                )}
                                <Button
                                    onClick={() => {
                                        modal.confirm({
                                            title: i18n.translate({ id: 'dlp.confirm.title', sourceName: 'Chat' }),
                                            content: i18n.translate({ id: 'dlp.confirm.text', sourceName: 'Chat' }),
                                            confirmText: i18n.translate({
                                                id: 'dlp.confirm.button',
                                                sourceName: 'Chat',
                                            }),
                                            onConfirm: () => {
                                                setShowPiiText(false);
                                                ChatInputActions.handleDlpChange({
                                                    target: { checked: false },
                                                } as React.ChangeEvent<HTMLInputElement>);
                                                setShouldSendAfterDlpChange(true);
                                            },
                                        });
                                    }}
                                    className="text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover"
                                    variant="secondary"
                                >
                                    <div className="flex items-center gap-2">
                                        <DlpInactive size={20} />
                                        <span>
                                            {i18n.translate({ id: 'dlp.button.send.original', sourceName: 'Chat' })}
                                        </span>
                                    </div>
                                </Button>
                                <button
                                    onClick={() => {
                                        handleTextareaClick();
                                    }}
                                    className="flex items-center gap-2 mt-3"
                                >
                                    <span>{i18n.translate({ id: 'dlp.button.cancel', sourceName: 'Chat' })}</span>
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <MobileRecordButton
                setNewMessage={setNewMessage}
                newMessage={newMessage}
                onRecordingChange={setIsRecording}
                onVolumeChange={setAudioVolume}
                i18n={i18n}
            />

            {giveExamples && (
                <div
                    className={`hidden sm:flex absolute px-3 transition-opacity duration-100 ${
                        showExamples ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    } ${'top-[calc(16rem+numOfLines*2rem)]'} `}
                >
                    <ExampleList
                        assistantExamples={selectedPrompt!.examples}
                        handleExampleClick={ChatInputActions.handleExampleClick}
                        messagesLength={newMessage ? newMessage.length : 0}
                        tmpImageUrlsLength={tmpImageUrls.length}
                        tempFileNamesLength={tmpFileNames.length}
                    />
                </div>
            )}
        </>
    );
}
